import React from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, ArrowRight, Play,
  DollarSign, Shield, RefreshCw, BarChart3,
  Lock, TrendingUp, Globe, Home, HeartHandshake, Percent, Gavel, Briefcase, FolderOpen, Users, Diamond, Crown, Stamp
} from 'lucide-react';
import { Logo } from '../common';
import Footer from '../common/Footer';
import YieldDifferentialExplorer from '../common/YieldDifferentialExplorer';
import useMetaTags from '../../hooks/useMetaTags';

// Two-tone text formatting component — inherits parent font weight
const ForwardsflowText = ({ variant = 'default' }) => (
  <span className="font-sans inline">
    <span className={variant === 'white' ? 'text-white' : 'text-black'}>Forwards</span>
    <span className={variant === 'white' ? 'text-zinc-300' : 'text-zinc-500'}>flow</span>
  </span>
);

const formatBrandText = (text, variant = 'default') => {
  if (typeof text !== 'string') return text;
  const tokens = [];
  let currentIdx = 0;
  const regex = /(Forwardsflow)/gi;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > currentIdx) {
      tokens.push(text.substring(currentIdx, match.index));
    }
    tokens.push(<ForwardsflowText key={match.index} variant={variant} />);
    currentIdx = regex.lastIndex;
  }

  if (currentIdx < text.length) {
    tokens.push(text.substring(currentIdx));
  }
  return tokens.length > 0 ? tokens : text;
};

const AboutPage = () => {
  useMetaTags({
    title: 'ForwardsFlow | About',
    description: 'One platform for the whole life of a cross-border cashflow. Yield discovery, exchange rates, compliance fulfilment, hedging devices, liquidity preservation, transaction execution and portfolio management across 6 markets.',
    canonical: 'https://www.forwardsflow.com/about',
  });

  // Feature Cards — 6 cards
  const featureCards = [
    { icon: Gavel, title: 'T-bill auction access', description: 'Participate in frontier economy treasury bill auctions directly. View upcoming auctions, submit competitive or non-competitive bids, and track allocations.' },
    { icon: Shield, title: 'Exchange rate forwards protection', description: 'Lock in exchange rates for the return leg via pooled forwards contracts. Protect repatriation value when you realise T-bill proceeds.' },
    { icon: FolderOpen, title: 'Digital depository settlement', description: 'Direct settlement to your securities depository account via SWIFT instruction. Securities held in your name at the relevant national central depository.' },
    { icon: RefreshCw, title: 'Automated rollover', description: 'Advance notice before maturity. Opt in to rebid at the next auction cycle automatically. 91-day cycles with configurable rollover preferences.' },
    { icon: Briefcase, title: 'Portfolio dashboard', description: 'Track holdings, yields, maturity dates, and foreign exchange positions in real time. View auction calendar, forwards pool status, and settlement timeline.' },
    { icon: Users, title: 'Bulk account opening', description: 'Pre-KYC approval and digital depository account setup via partner banks. Open your investment account remotely.' },
  ];

  // Why Us — 5 subsections
  const whySubsections = [
    { icon: Diamond, title: 'Structural yield premium', body: 'Frontier economy T-bills yield up to 14.8% vs UK gilts at 3.9% — a significant yield premium. This spread is structural, driven by monetary policy and sovereign borrowing needs, not a temporary anomaly.' },
    { icon: Crown, title: 'Sovereign guarantee', body: 'Treasury bills are backed by the full faith and credit of sovereign governments. Local-currency treasury paper represents the lowest-risk fixed income instrument in each market.' },
    { icon: Stamp, title: 'Cross-border access gap', body: 'International investors can\'t easily buy frontier economy T-bills from abroad. Local brokers require in-person visits, local bank accounts, and manual depository registration. Forwardsflow eliminates these barriers.' },
    { icon: Shield, title: 'Exchange rate protected returns', body: 'Pooled forwards contracts protect the value of your returns when converting back to your base currency. Lock in your divestment rate at the point of investment or at maturity.' },
    { icon: RefreshCw, title: '3-month rollover cycle', body: '91-day tenor with automated re-investment. Receive advance notice before maturity, choose to rollover (rebid at next auction) or realise profits with exchange rate forwards protection.' },
  ];

  // How It Works — 7 pillars
  const howSteps = [
    { step: '01', title: 'Yield discovery.', description: 'Compare structural frontier yields against your home benchmark, net of fees and FX. View upcoming treasury bill auctions, current yields, and historical trends.' },
    { step: '02', title: 'Exchange rates.', description: 'Live spot, forward curves and competitive bank quotes via RFQ. Get integrated foreign exchange forwards pricing from partner banks.' },
    { step: '03', title: 'Compliance fulfilment.', description: 'Remote KYC, CDS account opening and per-market rule sets. Open your securities depository account from abroad — no in-country visit required.' },
    { step: '04', title: 'Hedging devices.', description: 'Pooled forwards, hedge ratios and FX overlay to lock in your exit rate. Opt into the forwards pool to lock your divestment foreign exchange rate.' },
    { step: '05', title: 'Liquidity preservation.', description: 'Maturity ladders, rollover instructions, netting and a secondary market for early exit. Auto-rollover to the next auction cycle or realise profits.' },
    { step: '06', title: 'Transaction execution.', description: 'Place competitive or non-competitive bids at treasury bill auctions. Settlement instructions and CSD confirmation with a full lifecycle audit trail.' },
    { step: '07', title: 'Portfolio management.', description: 'Holdings, performance, risk, FX overlay, governance and projections in one view. Track your positions in real time.' },
  ];

  // Target Investor Segments — 4 cards aligned to launch markets
  const investorSegments = [
    { icon: Globe, title: 'International investors', description: 'Invest in frontier economy government securities from anywhere in the world. Built for international professionals in the UK, UAE, US, Japan, and Geneva — including NHS staff, Gulf-based professionals, international organisation employees, Tokyo-based professionals, and globally mobile investors. Open a depository account remotely, bid at auction, and protect your returns with foreign exchange forwards. No local broker. No in-country visit.' },
    { icon: Building2, title: 'International organisation staff', description: 'Purpose-built for professionals at the UN, UNEP, UN-Habitat, and international bodies in Geneva, Vienna, New York, Tokyo, and Nairobi. Whether you\'re building property back home or positioning for return, invest in frontier economy government T-bills for real asset-liability matching. Direct CSD settlement and automated 91-day rollover.' },
    { icon: Home, title: 'Family offices', description: 'Allocate to frontier economy sovereign fixed income with institutional-grade infrastructure. Forwardsflow serves family offices in the US, UK, Switzerland, Japan, and Dubai seeking structural yield premiums of 5–23% above OECD benchmarks. Direct depository settlement, pooled foreign exchange forwards for better pricing, and portfolio analytics across 9 frontier markets.' },
    { icon: HeartHandshake, title: 'Impact & fixed income allocators', description: 'Channel capital into frontier economy sovereign debt that funds national development. Frontier economy T-bills yield 8–45% with structural premiums over UK gilts. Automated rollover, foreign-exchange-hedged return estimation, full compliance automation, and scalable access across 6 markets via a single platform.' },
  ];

  // System Specifications — 7 rows (by pillar)
  const specifications = [
    { capability: 'Yield discovery', detail: '91-day Treasury bills (primary) across 9 frontier markets. Compare structural yields against OECD benchmarks, net of fees and FX.' },
    { capability: 'Exchange rates', detail: 'Live spot, 1–12M forward curves, central bank reference rates. Invest in USD, CHF, AED, GBP & JPY. Competitive RFQ-sourced pricing.' },
    { capability: 'Compliance fulfilment', detail: 'Remote KYC via Forwardsflow. Bank-facilitated CDS account opening. Per-market rule sets. Automated AML screening and investor verification.' },
    { capability: 'Hedging devices', detail: 'Pooled forwards contracts for better pricing via economies of scale. Lock in divestment exchange rate at investment or maturity.' },
    { capability: 'Liquidity preservation', detail: 'Maturity ladders, automated 91-day rollover, netting preview, secondary market for early exit, repatriation pipeline via SWIFT.' },
    { capability: 'Transaction execution', detail: 'Competitive and non-competitive auction bids. Direct SWIFT settlement (MT103, MT300, MT544, MT546). Full lifecycle audit trail.' },
    { capability: 'Portfolio management', detail: 'Real-time dashboard with holdings, performance, risk, FX overlay, governance, projections and exports.' },
  ];

  // Markets Available — yields reflect highest 91-day T-bill rate in the past 1 year
  const markets = [
    { name: 'Kenya', flag: '/flags/ke.png', status: 'live', badge: 'LIVE', details: '91-day T-bills yielding up to 16.84% with direct depository settlement' },
    { name: 'India', flag: '/flags/in.png', status: 'coming_soon', badge: 'COMING SOON', details: '91-day T-bills yielding up to 7.2%. Government T-bill and G-Sec access with exchange rate forwards protection' },
    { name: 'Brazil', flag: '/flags/br.png', status: 'coming_soon', badge: 'COMING SOON', details: '91-day T-bills yielding up to 14.15%. Tesouro Direto treasury bill access with exchange rate forwards protection' },
    { name: 'Mexico', flag: '/flags/mx.png', status: 'coming_soon', badge: 'COMING SOON', details: '91-day CETES yielding up to 11.5%. Treasury bill access with exchange rate forwards protection' },
    { name: 'Turkey', flag: '/flags/tr.png', status: 'coming_soon', badge: 'COMING SOON', details: '91-day T-bills yielding up to 50%. Turkish treasury bill access with exchange rate forwards protection' },
    { name: 'Bangladesh', flag: '/flags/bd.png', status: 'coming_soon', badge: 'COMING SOON', details: '91-day T-bills yielding up to 11%. Bangladesh treasury bill access with exchange rate forwards protection' },
    { name: 'South Africa', flag: '/flags/za.png', status: 'coming_soon', badge: 'COMING SOON', details: '91-day T-bills yielding up to 8.25%. Government bond and T-bill access with exchange rate forwards protection' },
    { name: 'Egypt', flag: '/flags/eg.png', status: 'coming_soon', badge: 'COMING SOON', details: '91-day T-bills yielding up to 32%. Egyptian treasury bill access with exchange rate forwards protection' },
    { name: 'Ethiopia', flag: '/flags/et.png', status: 'coming_soon', badge: 'COMING SOON', details: '91-day T-bills yielding up to 28%. Treasury bill access with exchange rate forwards protection' },
  ];

  return (
    <div className="min-h-screen bg-white text-black font-sans antialiased">

      {/* Skip navigation link for accessibility */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:px-4 focus:py-2 focus:bg-white focus:text-black focus:border focus:border-zinc-300">Skip to main content</a>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-zinc-200" aria-label="Main navigation" role="navigation">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-20">
            <Logo />
            <div className="hidden lg:flex items-center gap-6">
              <a href="#how-it-works" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">How It Works</a>
              <Link to="/pricing" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Pricing</Link>
              <Link to="/apply" className="font-mono text-xs uppercase tracking-[0.15em] text-white bg-[#4D4D4D] hover:bg-black px-3 py-1.5 transition-colors">Open Account</Link>
              <Link to="/demo" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-700 hover:text-black flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 hover:border-[#4D4D4D] transition-all">
                <Play className="w-3 h-3 fill-black text-black" /> Book a Demo
              </Link>
              <Link to="/register/bank" className="font-mono text-xs uppercase tracking-[0.15em] text-[#3832e3] hover:text-[#2f29c7] px-3 py-1.5 border border-[#3832e3] hover:border-[#2f29c7] transition-all">Banks</Link>
              <Link to="/contact" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Contact</Link>
              <Link to="/login" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-800 hover:text-black font-medium">Platform Login</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main id="main-content" role="main">

        {/* SEGMENT 1 — Hero (bg-white) */}
        <section className="pt-36 pb-24 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-4xl mx-auto">
              <div className="flex items-center justify-center gap-1.5 px-2 py-1 border border-zinc-300 rounded-full font-sans text-[8px] sm:text-xs md:text-sm tracking-normal text-zinc-600 mb-8 animate-fade-in w-fit mx-auto">
                <span className="w-2 h-2 bg-[#4D4D4D] rounded-full animate-pulse flex-shrink-0"></span>
                <span className="flex-shrink-0 whitespace-nowrap">Full stack cross border cashflow management.</span>
              </div>
              <h1 className="font-sans text-xs sm:text-base md:text-lg lg:text-[1.2rem] xl:text-[1.35rem] font-bold text-[#4D4D4D] tracking-tight mb-8 text-center max-w-5xl mx-auto space-y-1.5">
                <span className="block leading-snug">One platform for the whole life of a cross-border cashflow.</span>
              </h1>
              <p className="font-sans text-xs sm:text-sm md:text-base lg:text-lg text-zinc-600 mb-10 text-center max-w-4xl mx-auto font-normal leading-relaxed">
                Yield discovery, exchange rates, compliance fulfilment, hedging devices, liquidity preservation, transaction execution and portfolio management — one platform for cross-border cashflows across 6 markets.
              </p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <Link
                  to="/apply"
                  className="px-8 py-3.5 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] hover:bg-black transition-all flex items-center gap-2"
                >
                  Open Account <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/demo"
                  className="px-8 py-3.5 bg-transparent text-black font-mono text-xs uppercase tracking-[0.15em] font-medium border border-zinc-300 hover:border-[#4D4D4D] transition-all flex items-center gap-2"
                >
                  Book a Demo
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* SEGMENT 2 — Yield Differential Explorer (bg-zinc-50) */}
        <section id="yield-explorer" className="py-24 border-t border-zinc-200 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-3">Yield comparison tool</span>
              <h2 className="font-sans text-2xl md:text-3xl font-bold text-black tracking-tight leading-tight mb-6">
                Frontier economy yield calculator.
              </h2>
              <p className="text-zinc-600 leading-relaxed font-sans max-w-4xl mx-auto text-[10px] sm:text-xs md:text-sm whitespace-nowrap">
                Compare exchange rate adjusted, structural frontier yield with OECD reserve staples.
              </p>
            </div>
            <YieldDifferentialExplorer />
          </div>
        </section>

        {/* SEGMENT 3 — Value Proposition Grid (bg-white) */}
        <section className="py-24 border-t border-zinc-200 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featureCards.map((feat, idx) => (
                <div key={idx} className="border border-zinc-300 p-8 bg-white transition-all hover:border-[#4D4D4D] hover:-translate-y-0.5">
                  <div className="w-10 h-10 border border-zinc-300 flex items-center justify-center mb-6 bg-zinc-50">
                    <feat.icon className="w-5 h-5 text-black" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-sans">{feat.title}</h3>
                  <p className="text-xs text-gray-600 leading-relaxed font-sans">{feat.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SEGMENT 4 — Market Impact / Why Us (bg-zinc-50) */}
        <section className="py-24 border-t border-zinc-200 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid lg:grid-cols-3 gap-16">

              {/* Left Column: Heading */}
              <div className="lg:col-span-1">
                <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-3">Market impact</span>
                <h2 className="font-sans text-3xl md:text-4xl font-bold text-black tracking-tight leading-tight">
                  Why frontier economy T-bills — and why now.
                </h2>
              </div>

              {/* Right Columns: Core items */}
              <div className="lg:col-span-2 space-y-12">
                {whySubsections.map((box, idx) => (
                  <div key={idx} className="flex gap-6 items-start">
                    <div className="w-10 h-10 border border-zinc-300 flex-shrink-0 flex items-center justify-center bg-white">
                      <box.icon className="w-5 h-5 text-black" />
                    </div>
                    <div>
                      <h3 className="font-sans text-xl font-bold text-black mb-2">{box.title}</h3>
                      <p className="text-xs text-zinc-600 leading-relaxed font-sans">{formatBrandText(box.body)}</p>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </section>

        {/* SEGMENT 5 — How It Works (bg-white) */}
        <section id="how-it-works" className="py-24 border-t border-zinc-200 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-16">
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-3">Operational pipeline</span>
              <h2 className="font-sans text-3xl font-bold text-black">{formatBrandText("How Forwardsflow works.")}</h2>
            </div>

            <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-8">
              {howSteps.map((step, idx) => (
                <div key={idx} className="border border-zinc-300 p-6 bg-zinc-50 relative">
                  <div className="absolute top-4 right-4 font-mono text-xs text-zinc-400 font-semibold">{step.step}</div>
                  <h3 className="font-sans text-sm font-bold text-black mb-3 pr-8">{step.title}</h3>
                  <p className="text-xs text-zinc-600 leading-relaxed font-sans">{formatBrandText(step.description)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SEGMENT 6 — Target Investor Segments (bg-zinc-50) */}
        <section className="py-24 border-t border-zinc-200 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-16">
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-3">Who we serve</span>
              <h2 className="font-sans text-3xl md:text-4xl font-bold text-black tracking-tight leading-tight">
                Built for international investors and family offices.
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {investorSegments.map((seg, idx) => (
                <div key={idx} className="border border-zinc-300 p-8 bg-white transition-all hover:border-[#4D4D4D] hover:-translate-y-0.5">
                  <div className="w-10 h-10 border border-zinc-300 flex items-center justify-center mb-6 bg-zinc-50">
                    <seg.icon className="w-5 h-5 text-black" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-sans">{seg.title}</h3>
                  <p className="text-xs text-gray-600 leading-relaxed font-sans">{seg.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SEGMENT 7 — CTA (bg-zinc-50) */}
        <section className="py-20 border-t border-zinc-200 bg-zinc-50 text-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="border border-zinc-300 bg-white p-12">
              <div className="max-w-3xl mx-auto">
                <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-3">Get started</span>
                <h3 className="font-sans text-3xl font-bold text-black mb-4">Ready to manage your cross-border cashflows end to end?</h3>
                <p className="text-[10px] sm:text-xs md:text-sm font-sans text-zinc-600 mb-8 max-w-5xl mx-auto leading-relaxed whitespace-nowrap">
                  {formatBrandText("Join international investors and family offices already using Forwardsflow for full stack cross border cashflow management.")}
                </p>
                <div className="flex gap-4 justify-center">
                  <Link
                    to="/apply"
                    className="px-8 py-3.5 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] hover:bg-black transition-all"
                  >
                    Apply for Access
                  </Link>
                  <Link
                    to="/demo"
                    className="px-8 py-3.5 bg-transparent text-black font-mono text-xs uppercase tracking-[0.15em] font-medium border border-zinc-300 hover:border-[#4D4D4D] transition-all"
                  >
                    Book a Demo
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SEGMENT 8 — System Specifications Table (bg-white) */}
        <section id="specifications" className="py-24 border-t border-zinc-200 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-16">
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-3">System specifications</span>
              <h2 className="font-sans text-3xl font-bold text-black">Platform specifications.</h2>
            </div>

            <div className="border border-zinc-300 overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-300 font-mono text-xs uppercase tracking-wider text-zinc-500">
                    <th className="px-6 py-4 font-semibold">Capability</th>
                    <th className="px-6 py-4 font-semibold">Specification details</th>
                  </tr>
                </thead>
                <tbody>
                  {specifications.map((row, idx) => (
                    <tr key={idx} className="border-b border-zinc-200 last:border-b-0 hover:bg-zinc-50 transition-colors">
                      <td className="px-6 py-4 font-mono text-xs font-semibold text-black uppercase tracking-wider">{row.capability}</td>
                      <td className="px-6 py-4 text-sm text-zinc-700 font-sans">{row.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* SEGMENT 9 — Markets Available (bg-zinc-50) */}
        <section className="py-24 border-t border-zinc-200 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-16">
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-3">Markets available</span>
              <h2 className="font-sans text-3xl md:text-4xl font-bold text-black tracking-tight leading-tight">
                Markets available.
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {markets.map((market, idx) => (
                <div
                  key={idx}
                  className={`border p-6 transition-all ${
                    market.status === 'live'
                      ? 'border-[#4D4D4D] bg-white'
                      : 'border-zinc-300 bg-white/60'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      <span className="w-6 h-4 overflow-hidden inline-block flex-shrink-0">
                        <img
                          src={market.flag}
                          alt={`${market.name} flag`}
                          loading="lazy"
                          className={`w-full h-full object-cover scale-[1.15] ${market.status !== 'live' ? 'grayscale' : ''}`}
                        />
                      </span>
                      <h3 className="font-sans text-lg font-bold text-black">{market.name}</h3>
                    </div>
                    <span
                      className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border ${
                        market.status === 'live'
                          ? 'border-[#4D4D4D] text-black bg-zinc-100 font-bold'
                          : 'border-zinc-300 text-zinc-500'
                      }`}
                    >
                      {market.badge}
                    </span>
                  </div>
                  {market.details && (
                    <p className="text-xs text-zinc-600 leading-relaxed font-sans">{market.details}</p>
                  )}
                  {market.status === 'coming_soon' && (
                    <p className="text-xs text-zinc-400 font-mono uppercase tracking-wider mt-2">Coming soon</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
